import { TERMINAL_SUBMIT_ERRORS, type SubmissionPayload } from '@prism/sim';
import { PENDING_KEY, type StorageLike } from './save.js';

/**
 * Score submission (TRD-10 §5).
 *
 * The payload shape is the server's `SubmissionSchema` verbatim — the two are a contract,
 * and a client-side type that merely resembles it would drift into 400s that look like
 * cheating.
 */

export type { SubmissionPayload };

export type SubmitOutcome =
  | { status: 'accepted'; rank?: number }
  | { status: 'queued'; reason: string }
  | { status: 'rejected'; code: string; message: string };

/**
 * What to TELL THE PLAYER about a submission, in words they can act on.
 *
 * A failed submission used to be entirely silent: the outcome was reduced to a boolean
 * and thrown away, so "my run never showed up on the leaderboard" was unactionable from
 * both sides — the player saw an empty board and the logs said nothing. Every one of
 * these lines names the thing to DO, because a code on its own is not a diagnosis.
 */
export function submitMessage(outcome: SubmitOutcome): { tone: 'ok' | 'warn'; text: string } {
  if (outcome.status === 'accepted') {
    return {
      tone: 'ok',
      text: outcome.rank === undefined ? 'Score submitted.' : `Score submitted — rank ${outcome.rank}.`,
    };
  }
  if (outcome.status === 'queued') {
    return { tone: 'warn', text: 'Could not reach the server — saved, and sent next time you play.' };
  }
  switch (outcome.code) {
    case 'STALE_CLIENT':
      return { tone: 'warn', text: 'Not counted: the server updated mid-run. Reload the page.' };
    case 'RATE_LIMITED':
      return { tone: 'warn', text: 'Not counted: too many runs submitted this hour.' };
    case 'BAD_DATE':
    case 'INVALID_DATE':
      return { tone: 'warn', text: "Not counted: this run is not for today's daily." };
    case 'BAD_NAME':
      return { tone: 'warn', text: 'Not counted: that display name was rejected.' };
    case 'REPLAY_FAILED':
      return {
        tone: 'warn',
        text: 'Not counted yet: the server hit a bug replaying this run. Saved — it will be sent again.',
      };
    case 'SCORE_MISMATCH':
    case 'HASH_MISMATCH':
      return { tone: 'warn', text: `Not counted: the server replayed this run differently (${outcome.code}).` };
    default:
      return { tone: 'warn', text: `Not counted (${outcome.code}).` };
  }
}

export interface SubmitDeps {
  fetch: typeof fetch;
  storage: StorageLike;
  /** Injected so tests do not sleep through the backoff. */
  sleep: (ms: number) => Promise<void>;
  baseUrl: string;
}

const MAX_ATTEMPTS = 3;

/**
 * Codes the server returns that a retry cannot fix — the shared list from `@prism/sim`.
 *
 * `STALE_CLIENT` in particular must NOT be retried or queued — the client is running a
 * different simulation than the server, so every replay of this log will diverge. The
 * right response is a page reload, and telling the player anything about cheating would
 * be both wrong and insulting (TRD-11 R-11-10).
 *
 * It used to be a hand-maintained copy here, and it had drifted: it named two codes the
 * server never sends and omitted four it does.
 */
const TERMINAL_CODES = TERMINAL_SUBMIT_ERRORS;

export async function submitRun(
  payload: SubmissionPayload,
  deps: SubmitDeps,
): Promise<SubmitOutcome> {
  let lastError = 'network';

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) await deps.sleep(250 * 2 ** (attempt - 1));

    let response: Response;
    try {
      response = await deps.fetch(`${deps.baseUrl}/api/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch {
      lastError = 'network';
      continue;
    }

    if (response.ok) {
      const body = (await response.json().catch(() => ({}))) as { rank?: number };
      return body.rank === undefined
        ? { status: 'accepted' }
        : { status: 'accepted', rank: body.rank };
    }

    // `error` and `detail` are what the route actually sends. This read `code` and
    // `message`, which the server has never used — so EVERY rejection arrived as
    // `HTTP_409`/`HTTP_422`, matched nothing in the terminal list, and was retried three
    // times and then queued forever. A stale client re-sent a doomed payload on every
    // boot for the life of the install, and the player was never told why their run did
    // not count. Legacy keys stay as a fallback; they cost nothing.
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
      detail?: string;
      code?: string;
      message?: string;
    };
    const code = body.error ?? body.code ?? `HTTP_${response.status}`;
    const message = body.detail ?? body.message ?? 'submission rejected';

    if (TERMINAL_CODES.has(code)) {
      return { status: 'rejected', code, message };
    }

    // 429 and 5xx are worth retrying; the loop backs off and tries again.
    lastError = code;
  }

  // A player who finishes a strong daily on a flaky connection should not lose it
  // (R-10-19). Queued now, drained on next boot.
  enqueue(deps.storage, payload);
  return { status: 'queued', reason: lastError };
}

export function enqueue(storage: StorageLike, payload: SubmissionPayload): void {
  const queue = readQueue(storage);
  // One entry per date: a replayed daily replaces its predecessor rather than stacking.
  const next = queue.filter((p) => p.date !== payload.date);
  next.push(payload);
  try {
    storage.setItem(PENDING_KEY, JSON.stringify(next));
  } catch {
    /* storage full or disabled — the run is lost, but the game keeps running */
  }
}

export function readQueue(storage: StorageLike): SubmissionPayload[] {
  try {
    const raw = storage.getItem(PENDING_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SubmissionPayload[]) : [];
  } catch {
    return [];
  }
}

export function clearQueue(storage: StorageLike): void {
  try {
    storage.removeItem(PENDING_KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * Drain the pending queue.
 *
 * Entries that the server terminally rejects are DROPPED rather than retried forever —
 * a stale-client submission will never validate, and keeping it would mean re-sending a
 * doomed payload on every boot for the life of the install.
 */
export async function drainQueue(deps: SubmitDeps): Promise<{ sent: number; kept: number }> {
  const queue = readQueue(deps.storage);
  if (queue.length === 0) return { sent: 0, kept: 0 };

  clearQueue(deps.storage);
  let sent = 0;
  const keep: SubmissionPayload[] = [];

  for (const payload of queue) {
    const outcome = await submitRun(payload, { ...deps, storage: new NullStorage() });
    if (outcome.status === 'accepted') sent++;
    else if (outcome.status === 'queued') keep.push(payload);
    // 'rejected' is terminal: dropped on purpose.
  }

  for (const payload of keep) enqueue(deps.storage, payload);
  return { sent, kept: keep.length };
}

/** Swallows writes, so draining cannot re-enqueue into the queue it is draining. */
class NullStorage implements StorageLike {
  getItem(): string | null {
    return null;
  }
  setItem(): void {
    /* discard */
  }
  removeItem(): void {
    /* discard */
  }
}
